import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientDataTree';
import ImageLeaf from './ImageLeaf';

type DatasetTreeProps = {
    patient: Patient;
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onSelect: React.Dispatch<React.SetStateAction<SelectedDatasets>>;
};

export type StructureSetLeafData = {
    data: StructureSet;
    doses: DoseMatrix[];
};

export type DatasetTree = {
    image: Image3D;
    registrations: RigidRegistration[];
    structureSets: StructureSetLeafData[];
};

const transformToTree = (patient: Patient): DatasetTree[] => {
    const sortedImages = [...patient.images].sort((a, b) => {
        const dateA = new Date(a.seriesMetadata.seriesDate ?? 0);
        const dateB = new Date(b.seriesMetadata.seriesDate ?? 0);
        return dateA.getTime() - dateB.getTime();
    });

    const tree = sortedImages.map((image) => {
        const structureSets = patient.structureSets
            .filter((ss) => ss.image?.dicomUid === image.dicomUid)
            .map((ss) => {
                const doses = patient.doses.filter(
                    (dose) => dose.structureSet?.dicomUid === ss.dicomUid
                );
                return { data: ss, doses };
            });
        const registrations = patient.registrations.filter(
            (reg) => reg.sourceFrameOfReferenceUid === image.frameOfReferenceUid
        );
        return { image, registrations, structureSets };
    });

    return tree;
};

const DatasetTree = ({
    patient,
    selectedDatasets,
    activeItems,
    onSelect,
}: DatasetTreeProps) => {
    const patientDatasetTree = transformToTree(patient);
    return patientDatasetTree.map((dataset) => (
        <ImageLeaf
            key={dataset.image.dicomUid}
            dataset={dataset}
            selectedDatasets={selectedDatasets}
            activeItems={activeItems}
            onSelect={onSelect}
            patient={patient}
        />
    ));
};

export default DatasetTree;
